<template>
  <nav
    class="c-lnb pc"
    v-show="!isMobile"
  >
    <div class="c-lnb__list">
      <template
        v-for="(route, index) in routes"
        :key="index"
      >
        <router-link
          v-if="route?.showCondition ?? true"
          :to="route.path"
          active-class="bg-[#007f70]"
          class="c-lnb__item"
          @click="trackNavigation(route)"
        >
          <i :class="route.iconClass"></i>
          <span class="c-lnb__tooltip">{{ route.tooltip }}</span>
        </router-link>
      </template>
    </div>
  </nav>
  <button @click="checkShowMenu()"></button>
  <nav
    class="c-lnb mo"
    v-show="isMobile"
    :class="{ on: showMenu }"
    :key="menuKey"
  >
    <div class="c-lnb__list">
      <template
        v-for="(route, index) in routes"
        :key="index"
      >
        <router-link
          v-if="route?.showCondition ?? true"
          :to="route.path"
          class="c-lnb__item"
          @click="trackNavigation(route)"
        >
          <div class="c-lnb__item-wrap">
            <i :class="route.iconClass"></i>
            <h2>{{ route.tooltip }}</h2>
          </div>
        </router-link>
      </template>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch, inject } from 'vue';
import { useRoute } from 'vue-router';
import { roleStore } from '@/stores/role';

import { projectStore } from '@/stores/project';

import { $t } from '@/plugins/translation';

const $ga = inject('$ga') as any;
const roleModule = roleStore();
const projectModule = projectStore();
const selectedProjectIdx = computed(() => projectModule.selectedProjectId || 0);

// const isDev = computed(
//   () => process.env.VUE_APP_MODE === 'DEV' || process.env.VUE_APP_MODE === 'LOCAL',
// );

const routes = computed(() => [
  { path: '/', iconClass: 'c-lnb__icon', tooltip: $t('common', 'home') },
  // {
  //   path: '/recruitment',
  //   iconClass: 'c-lnb__icon c-lnb__icon--union',
  //   tooltip: $t('component.header', 'recruitment'),
  //   showCondition: isDev.value,
  // },
  {
    path: `/dashboard/${selectedProjectIdx.value}`,
    iconClass: 'c-lnb__icon c-lnb__icon--dashboard',
    tooltip: $t('component.header', 'dashboard'),
  },
  // {
  //   path: `/pilot/${selectedProjectIdx.value}`,
  //   iconClass: 'c-lnb__icon c-lnb__icon--dashboard',
  //   tooltip: $t('component.header', 'pilot'),
  // },
  {
    path: `/epic/${selectedProjectIdx.value}`,
    iconClass: 'c-lnb__icon c-lnb__icon--epic',
    tooltip: $t('common', 'epic'),
  },
  {
    path: `/timeline/${selectedProjectIdx.value}`,
    iconClass: 'c-lnb__icon c-lnb__icon--calendar',
    tooltip: $t('component.header', 'timeline'),
  },
  {
    path: `/request/${selectedProjectIdx.value}`,
    iconClass: 'c-lnb__icon c-lnb__icon--request',
    tooltip: $t('common', 'request'),
  },
  {
    path: calculateRoute.value,
    iconClass: 'c-lnb__icon c-lnb__icon--point',
    tooltip: $t('component.header', 'settlement'),
  },
  {
    path: `/doclist/${selectedProjectIdx.value}`,
    iconClass: 'c-lnb__icon c-lnb__icon--docs',
    tooltip: $t('common', 'doclist'),
    // showCondition: isDev.value,
  },
  {
    path: `/setting`,
    iconClass: 'c-lnb__icon c-lnb__icon--setting',
    tooltip: $t('common', 'setting'),
  },
]);
const calculateRoute = computed(() => {
  if (roleModule.isClient) {
    return `/calculate/client/${selectedProjectIdx.value}`;
  } else {
    return `/calculate/expert/${selectedProjectIdx.value}`;
  }
});

// check device
const isMobile = ref(false);
const showMenu = ref(false);

const checkIsMobile = () => {
  isMobile.value = window.innerWidth < 767;
};
const checkShowMenu = () => {
  showMenu.value = !showMenu.value;
};

const menuKey = ref(0);
const route = useRoute();

const trackNavigation = (routeItem: { path: string; tooltip: string }) => {
  try {
    $ga?.trackEvent?.('navigation_click', {
      label: routeItem.tooltip,
      path: routeItem.path,
      position: isMobile.value ? 'mobile_left_menu' : 'desktop_left_menu',
    });
  } catch (e) {
    // no-op
  }
};

watch(
  () => route.fullPath,
  () => {
    showMenu.value = false;
    menuKey.value += 1;
    setTimeout(() => {
      showMenu.value = false;
    }, 0);
  },
);

onMounted(() => {
  checkIsMobile();
});
</script>

<style lang="scss">
.c-lnb.pc {
  $this: '.c-lnb';
  grid-area: nav;
  width: 8rem;
  height: 100vh;
  padding: 10px;
  background: var(--main-50);
  .c-lnb__list {
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
  }
  .c-lnb__item {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    text-decoration: none;
    font-size: 18px;
    width: 6rem;
    height: 6rem;
    cursor: pointer;
    border-radius: var(--radius-4);
    position: relative;
    &:hover {
      background: var(--main-30);
      #{$this}__tooltip {
        display: block;
      }
    }
    &:focus {
      background: var(--main-30);
    }
  }
  .c-lnb__icon {
    display: inline-flex;
    width: 2rem;
    height: 2rem;
    background: url(@/assets/images/icon_hamberger.svg) center / contain no-repeat;
    &--dashboard {
      background: url(@/assets/images/icon_dashboard.svg) center / contain no-repeat;
    }
    &--epic {
      background: url(@/assets/images/icon_epic.svg) center / contain no-repeat;
    }
    &--calendar {
      background: url(@/assets/images/icon_calendar_white.svg) center / contain no-repeat;
    }
    &--point {
      background: url(@/assets/images/icon_point.svg) center / contain no-repeat;
    }
    &--request {
      background: url(@/assets/images/icon_request.svg) center / contain no-repeat;
    }
    &--setting {
      background: url(@/assets/images/icon_set.svg) center / contain no-repeat;
    }
    &--docs {
      background: url(@/assets/images/icon_doclist.svg) center / contain no-repeat;
    }
    &--union {
      width: 2.4rem;
      height: 2.4rem;
      background: url(@/assets/images/icon_union.svg) center / contain no-repeat;
    }
  }
  .c-lnb__tooltip {
    position: absolute;
    top: 50%;
    left: 110%;
    width: 100%;
    word-break: keep-all;
    line-height: 3rem;
    transform: translateY(-50%);
    background: #000;
    color: var(--white);
    text-align: center;
    font-size: var(--font-14);
    border-radius: 0.2rem;
    z-index: 110;
    display: none;
    &:after {
      content: '';
      position: absolute;
      top: 50%;
      right: 100%;
      margin-top: -5px;
      border-width: 5px;
      border-style: solid;
      border-color: transparent #000 transparent transparent;
    }
    &:hover {
      .c-tooltip__txt {
        visibility: visible;
      }
    }
  }
}
// mo
.c-lnb.mo {
  $this: '.c-lnb';
  grid-area: nav;
  position: absolute;
  z-index: 130;
  width: 27.2rem;
  height: 100vh;
  padding: 1.2rem 2.6rem;
  background: var(--main-50);
  left: -27.2rem;
  transition: left 0.3s ease-in-out;
  &.on {
    left: 0;
  }
  .c-lnb__list {
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
  }
  .c-lnb__item {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    text-decoration: none;
    font-size: 18px;
    width: 100%;
    height: 6rem;
    cursor: pointer;
    border-radius: var(--radius-4);
    position: relative;
    &-wrap {
      width: 100%;
      height: auto;
      display: flex;
      justify-content: start;
      align-items: center;
      flex-direction: row;
      gap: 25px;
      h2 {
        font-weight: 600;
        font-size: 1.8rem;
        line-height: 2.4rem;
        color: var(--white);
      }
      .router-link-active {
        &:focus {
          background: var(--main-30);
        }
      }
    }
  }
  .c-lnb__icon {
    display: inline-flex;
    width: 2rem;
    height: 2rem;
    background: url(@/assets/images/icon_hamberger.svg) center / contain no-repeat;
    &--dashboard {
      background: url(@/assets/images/icon_dashboard.svg) center / contain no-repeat;
    }
    &--epic {
      background: url(@/assets/images/icon_epic.svg) center / contain no-repeat;
    }
    &--calendar {
      background: url(@/assets/images/icon_calendar_white.svg) center / contain no-repeat;
    }
    &--point {
      background: url(@/assets/images/icon_point.svg) center / contain no-repeat;
    }
    &--setting {
      background: url(@/assets/images/icon_set.svg) center / contain no-repeat;
    }
    &--docs {
      background: url(@/assets/images/icon_doclist.svg) center / contain no-repeat;
    }
    &--union {
      width: 2.4rem;
      height: 2.4rem;
      background: url(@/assets/images/icon_union.svg) center / contain no-repeat;
    }
  }
  .c-lnb__tooltip {
    position: absolute;
    top: 50%;
    left: 110%;
    width: 8rem;
    height: 3rem;
    line-height: 3rem;
    transform: translateY(-50%);
    background: #000;
    color: var(--white);
    text-align: center;
    font-size: var(--font-14);
    border-radius: 0.2rem;
    z-index: 110;
    display: none;
    &:after {
      content: '';
      position: absolute;
      top: 50%;
      right: 100%;
      margin-top: -5px;
      border-width: 5px;
      border-style: solid;
      border-color: transparent #000 transparent transparent;
    }
    &:hover {
      .c-tooltip__txt {
        visibility: visible;
      }
    }
  }
}
</style>
